import React, { useState, useEffect } from "react";
import AdminSidebar from "./AdminSidebar";
import AdminTopBar from "./AdminTopBar";
import api from "./api/axios";
import { useActivityLogger } from "./hooks/useActivity";
import { useSearch } from "./contexts/SearchContext";

export default function AdminCategory() {
  const { searchQuery } = useSearch();
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // State untuk Modal (Add/Edit)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [formData, setFormData] = useState({ nama_kategori: "" });

  const { logAdminActivity } = useActivityLogger();

  // Fetch Categories
  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      // Sesuaikan endpoint dengan backend Anda: /kategori
      const res = await api.get("/kategori");
      setCategories(res.data);
    } catch (err) {
      console.error("Gagal mengambil kategori", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    logAdminActivity(
      "view",
      "view_admin_category_list",
      "category",
      null,
      "admin",
      "success",
      "",
      "Admin melihat daftar kategori"
    );
  }, []);

  // Filter Categories based on Search
  const filteredCategories = categories.filter((cat) =>
    cat.nama_kategori?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle Submit (Create / Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        // Update
        await api.put(`/kategori/${currentId}`, formData);
        logAdminActivity(
          "update",
          "update_category",
          "category",
          currentId,
          formData.nama_kategori,
          "success",
          "",
          `Admin update kategori ${formData.nama_kategori}`
        );
        alert("Kategori berhasil diperbarui");
      } else {
        // Create
        await api.post("/kategori", formData);
        logAdminActivity(
          "create",
          "create_category",
          "category",
          null,
          formData.nama_kategori,
          "success",
          "",
          `Admin membuat kategori ${formData.nama_kategori}`
        );
        alert("Kategori berhasil ditambahkan");
      }
      closeModal();
      fetchCategories();
    } catch (err) {
      console.error(err);
      alert(
        "Gagal menyimpan kategori: " +
          (err.response?.data?.error || err.message)
      );
    }
  };

  // Handle Delete
  const handleDelete = async (id, name) => {
    if (window.confirm(`Yakin ingin menghapus kategori "${name}"?`)) {
      try {
        await api.delete(`/kategori/${id}`);
        logAdminActivity(
          "delete",
          "delete_category",
          "category",
          id,
          name,
          "success",
          "",
          `Admin menghapus kategori ${name}`
        );
        alert("Kategori berhasil dihapus");
        fetchCategories();
      } catch (err) {
        console.error(err);
        alert(
          "Gagal menghapus kategori. Mungkin sedang digunakan oleh artikel/video."
        );
      }
    }
  };

  // Modal Helpers
  const openAddModal = () => {
    setIsEditing(false);
    setFormData({ nama_kategori: "" });
    setIsModalOpen(true);
  };

  const openEditModal = (cat) => {
    setIsEditing(true);
    setCurrentId(cat.ID);
    setFormData({ nama_kategori: cat.nama_kategori });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({ nama_kategori: "" });
  };

  return (
    <div className="min-h-screen flex">
      <AdminSidebar />
      <div className="flex-1 p-6 bg-gray-50">
        <AdminTopBar />

        <div className="bg-white p-4 rounded shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold">Manajemen Kategori</h2>
            <button
              onClick={openAddModal}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              + Tambah Kategori
            </button>
          </div>

          <table className="w-full text-sm">
            <thead className="border-b">
              <tr>
                <th className="py-2 text-left w-12">ID</th>
                <th className="py-2 text-left">Nama Kategori</th>
                <th className="py-2 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="3" className="py-4 text-center">
                    Loading...
                  </td>
                </tr>
              ) : filteredCategories.length === 0 ? (
                <tr>
                  <td colSpan="3" className="py-4 text-center">
                    Tidak ada kategori ditemukan.
                  </td>
                </tr>
              ) : (
                filteredCategories.map((cat) => (
                  <tr key={cat.ID} className="border-b hover:bg-gray-50">
                    <td className="py-3">{cat.ID}</td>
                    <td className="py-3 font-medium">{cat.nama_kategori}</td>
                    <td className="py-3 text-right">
                      <div className="flex justify-end items-center gap-2">
                        <button
                          onClick={() => openEditModal(cat)}
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <img
                            src="/icon-edit.png"
                            alt="Edit"
                            className="h-5 w-5"
                          />
                        </button>
                        <button
                          onClick={() =>
                            handleDelete(cat.ID, cat.nama_kategori)
                          }
                          className="p-1 hover:bg-gray-200 rounded"
                        >
                          <img
                            src="/icon-trash.png"
                            alt="Delete"
                            className="h-5 w-5"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Popup for Add/Edit */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-96">
            <h3 className="text-lg font-bold mb-4">
              {isEditing ? "Edit Kategori" : "Tambah Kategori Baru"}
            </h3>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Nama Kategori
                </label>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  value={formData.nama_kategori}
                  onChange={(e) =>
                    setFormData({ ...formData, nama_kategori: e.target.value })
                  }
                  required
                  autoFocus
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Simpan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
